import { Input, Modal, Radio, Space, Typography } from "antd";
import type React from "react";
import { useState } from "react";

const { Text } = Typography;

interface MFAOption {
  id: string;
  label: string;
}

interface MFACodeModalProps {
  visible: boolean;
  onSubmit: (code: string, method?: string) => void;
  onCancel: () => void;
  loading: boolean;
  mfaOptions?: MFAOption[];
}

const MFACodeModal: React.FC<MFACodeModalProps> = ({ visible, onSubmit, onCancel, loading, mfaOptions = [] }) => {
  const [code, setCode] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<string>(mfaOptions[0]?.id ?? "");

  const isMethodStep = mfaOptions.length > 0;

  // Sync default when mfaOptions changes (modal re-opens with new options)
  if (isMethodStep && !selectedMethod && mfaOptions[0]?.id) {
    setSelectedMethod(mfaOptions[0].id);
  }

  const handleOk = () => {
    if (isMethodStep && selectedMethod) {
      // Step 1: submit method only
      onSubmit("", selectedMethod);
    } else if (!isMethodStep && code.trim()) {
      // Step 2: submit code only
      onSubmit(code.trim());
    }
  };

  const handleAfterClose = () => {
    setCode("");
    setSelectedMethod("");
  };

  const isOkDisabled = isMethodStep ? !selectedMethod : !code.trim();

  return (
    <Modal
      title={isMethodStep ? "Select Verification Method" : "Enter Verification Code"}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText={isMethodStep ? "Continue" : "Submit"}
      cancelText="Cancel"
      okButtonProps={{ disabled: isOkDisabled, loading }}
      cancelButtonProps={{ disabled: loading }}
      width={440}
      afterClose={handleAfterClose}
      maskClosable={false}
      keyboard={false}
      zIndex={1000}
    >
      {isMethodStep ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Text>Choose how you want to verify your identity:</Text>
          </div>
          <Radio.Group value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value)}>
            <Space direction="vertical">
              {mfaOptions.map((opt) => (
                <Radio key={opt.id} value={opt.id}>
                  {opt.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Text>Enter the verification code to continue the transfer.</Text>
          </div>
          <Input
            placeholder="Enter verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onPressEnter={handleOk}
            maxLength={10}
            size="large"
            autoFocus
            disabled={loading}
          />
        </div>
      )}
    </Modal>
  );
};

export default MFACodeModal;
